
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TablePagination,
  Badge,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  People,
  CalendarToday,
  AccessTime,
  Link as LinkIcon,
  LocationOn,
  Person,
  Email,
  Phone,
  PhotoCamera,
} from '@mui/icons-material';
import axios from 'axios';

const CareerTalk_staff = () => {
  const [activeMainTab, setActiveMainTab] = useState(0);
  const [selectedCareerTalk, setSelectedCareerTalk] = useState(null);
  const [interestedColleges, setInterestedColleges] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [careerTalkDetailsOpen, setCareerTalkDetailsOpen] = useState(false);
  const [institutionNominationOpen, setInstitutionNominationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [careerTalks, setCareerTalks] = useState([]);
  const [activeCollegeTab, setActiveCollegeTab] = useState(0);

  // Rejection dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedCollegeForRejection, setSelectedCollegeForRejection] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');

  // Pagination states
  const [careerTalksPage, setCareerTalksPage] = useState(0);
  const [careerTalksRowsPerPage, setCareerTalksRowsPerPage] = useState(10);
  const [careerTalksTotal, setCareerTalksTotal] = useState(0);

  const [collegesPage, setCollegesPage] = useState(0);
  const [collegesRowsPerPage, setCollegesRowsPerPage] = useState(5);
  const [collegesTotal, setCollegesTotal] = useState(0);

  // Cache for loaded nominations to avoid re-fetching
  const [nominationsCache, setNominationsCache] = useState({});

  // API Configuration
  // const API_BASE_URL = 'http://192.168.2.212:8001/new_support/staff';
 

  const API_BASE_URL = 'https://erpapi.eduskillsfoundation.org/new_support/staff';

  const TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJlNDBmNWQ5NS04MzAwLTQ1MWEtYTZlZi0wYWZiN2FkNGFmYmMiLCJleHAiOjE3NjQyMjA5NDZ9.aHa-bWycHLAwVQ0CWUHkZvrVPEnuerB6jCj2hcNBC5k';
  const AUTH_TOKEN_DEFAULT = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJlNDBmNWQ5NS04MzAwLTQ1MWEtYTZlZi0wYWZiN2FkNGFmYmMiLCJleHAiOjE3NjQyMjA5NDZ9.aHa-bWycHLAwVQ0CWUHkZvrVPEnuerB6jCj2hcNBC5k';
  const ONLY_AUTH_TOKEN = localStorage.getItem("accessToken") || AUTH_TOKEN_DEFAULT;
  const AUTH_TOKEN = "Bearer " + ONLY_AUTH_TOKEN.toString();
  const axiosConfig = {
    headers: {
      'Authorization': `${AUTH_TOKEN}`,
      'Content-Type': 'application/json'
    }
  };

  // Transform API data to match component structure
  const transformCareerTalkData = (apiData) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map(item => ({
      id: item.id,
      career_talk_id: item.id,
      topic: item.topic,
      speaker: item.speaker,
      designation: item.designation,
      company: item.company,
      date: item.date,
      start_time: item.start_time,
      end_time: item.end_time,
      link: item.link,
      poster_url: item.poster_url,
      is_completed: item.is_completed,
      status: item.is_completed,
      location: "Virtual",
      pending_nominations: item.pending_nominations || 0
    }));
  };

  const transformCollegeData = (apiData, statusType = 'pending') => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map(item => {
      let status = statusType;
      if (item.is_staff_approval === '1') status = 'confirmed';
      if (item.is_staff_approval === '2') status = 'rejected';
      if (item.status) status = item.status;

      // Handle additional info for completed institutes
      const additionalInfo = item.additional_info || {};

      return {
        id: item.enrollment_id,
        enrollment_id: item.enrollment_id,
        career_talk_id: item.career_talk?.id || item.career_talk_id,
        name: item.institute?.name || 'Unknown Institute',
        location: item.institute?.state_name || 'Unknown Location',
        department: 'General',
        studentsCount: additionalInfo.students_attended || 0,
        preferredDate: item.participated_date || item.event_date || item.career_talk?.date,
        status: status,
        contactPerson: item.spoc?.name || 'Not specified',
        email: item.spoc?.email || 'Not specified',
        phone: item.spoc?.mobile || 'Not specified',
        institute_id: item.institute?.id,
        photograph_submitted: !!(additionalInfo.event_photos && additionalInfo.event_photos.length > 0),
        certificate_issued: additionalInfo.certificate_issued === 'Yes',
        is_staff_approval: item.is_staff_approval,
        career_talk: item.career_talk,
        created_at: item.created_at,
        // Additional fields for completed institutes
        additional_info: additionalInfo,
        participated_date: item.participated_date
      };
    });
  };

  const isEventLinkVisible = (talk) => {
    if (!talk.link) return false;
    const now = new Date();
    const eventDate = new Date(talk.date);
    return now.toDateString() === eventDate.toDateString();
  };

  // Fetch career talks
  const fetchCareerTalks = async (page = 0, limit = 10) => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      switch (activeMainTab) {
        case 0: // Upcoming
          endpoint = '/upcoming_career_talks';
          break;
        case 1: // Completed
          endpoint = '/completed_career_talks';
          break;
        case 2: // Cancelled
          endpoint = '/cancelled_career_talks';
          break;
        default:
          endpoint = '/upcoming_career_talks';
      }

      const paginatedEndpoint = `${endpoint}?page=${page + 1}&limit=${limit}`;
      console.log('Fetching career talks from:', `${API_BASE_URL}${paginatedEndpoint}`);

      const response = await axios.get(`${API_BASE_URL}${paginatedEndpoint}`, axiosConfig);
      console.log('Career talks API response:', response.data);

      const apiData = response.data.talks || [];
      const transformedData = transformCareerTalkData(apiData);

      setCareerTalks(transformedData);
      setCareerTalksTotal(response.data.total || transformedData.length);

    } catch (err) {
      console.error('Error fetching career talks:', err);
      setError(`Failed to fetch career talks: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch nominations by type using the specific career talk endpoint
  const fetchNominationsByType = async (type, careerTalkId, page = 1, limit = 100) => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      let statusType = 'pending';

      switch (type) {
        case 'pending':
          endpoint = `/pending/${careerTalkId}`;
          statusType = 'pending';
          break;
        case 'approved':
          endpoint = `/approved/${careerTalkId}`;
          statusType = 'confirmed';
          break;
        case 'rejected':
          endpoint = `/rejected/${careerTalkId}`;
          statusType = 'rejected';
          break;
        default:
          endpoint = `/pending/${careerTalkId}`;
      }

      const paginatedEndpoint = `${endpoint}?page=${page}&limit=${limit}`;
      console.log(`Fetching ${type} nominations from:`, `${API_BASE_URL}${paginatedEndpoint}`);

      const response = await axios.get(`${API_BASE_URL}${paginatedEndpoint}`, axiosConfig);
      console.log(`${type} nominations API response:`, response.data);

      const apiData = response.data[type] || [];
      const transformedData = transformCollegeData(apiData, statusType);

      console.log(`Transformed ${type} nominations:`, transformedData);

      // Update cache
      setNominationsCache(prev => ({
        ...prev,
        [`${careerTalkId}_${type}`]: transformedData
      }));

      // If this is the active tab, update the displayed colleges
      const currentTabType = ['pending', 'approved', 'rejected'][activeCollegeTab];
      if (type === currentTabType) {
        setInterestedColleges(transformedData);
        setCollegesTotal(response.data.total || transformedData.length);
        setCollegesPage(0);
      }

    } catch (err) {
      console.error(`Error fetching ${type} nominations:`, err);
      setError(`Failed to fetch ${type} nominations.`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch participated colleges for completed talks
  const fetchParticipatedColleges = async (careerTalkId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/completed_career_talk_2/${careerTalkId}?page=1&limit=100`, axiosConfig);

      console.log('Participated colleges API response:', response.data);

      // Use completed_institutes instead of approved
      const apiData = response.data.completed_institutes || [];
      const participatedColleges = transformCollegeData(apiData, 'confirmed');

      console.log('Transformed participated colleges:', participatedColleges);

      setInterestedColleges(participatedColleges);
      setCollegesTotal(response.data.pagination?.total_institutes || participatedColleges.length);
      setCollegesPage(0);

    } catch (err) {
      console.error('Error fetching participated colleges:', err);
      setError('Failed to fetch participated colleges data.');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change in nomination dialog
  const handleCollegeTabChange = async (newValue, careerTalkId) => {
    setActiveCollegeTab(newValue);
    setCollegesPage(0);

    const tabTypes = ['pending', 'approved', 'rejected'];
    const currentType = tabTypes[newValue];

    // Check if data is already in cache
    const cacheKey = `${careerTalkId}_${currentType}`;
    if (nominationsCache[cacheKey]) {
      console.log('Using cached data for:', cacheKey);
      setInterestedColleges(nominationsCache[cacheKey]);
      setCollegesTotal(nominationsCache[cacheKey].length);
    } else {
      // Fetch data for this tab
      console.log('Fetching data for tab:', currentType);
      await fetchNominationsByType(currentType, careerTalkId);
    }
  };

  // Load all nominations when dialog opens (but don't display until tab is selected)
  const loadInitialNominations = async (careerTalkId) => {
    if (activeMainTab === 1) { // Completed tab
      await fetchParticipatedColleges(careerTalkId);
    } else { // Upcoming tab - load pending nominations first
      await fetchNominationsByType('pending', careerTalkId);
    }
  };

  // Approve college
  const approveCollege = async (collegeId, careerTalkId) => {
    setLoading(true);
    setError(null);

    try {
      // await axios.put(`http://192.168.2.212:8001/new_support/approve_by_staff/${collegeId}`, {}, axiosConfig);
      await axios.put(`https://erpapi.eduskillsfoundation.org/new_support/approve_by_staff/${collegeId}`, {}, axiosConfig);
      // Update local state
      const updatedColleges = interestedColleges.map(college =>
        college.id === collegeId
          ? {
            ...college,
            status: 'confirmed',
            is_staff_approval: '1',
            confirmedDate: new Date().toISOString().split('T')[0]
          }
          : college
      );

      setInterestedColleges(updatedColleges);
      setSuccess('College approved successfully!');

      // Clear cache for this career talk to force refresh
      if (selectedCareerTalk) {
        const careerTalkId = selectedCareerTalk.id;
        Object.keys(nominationsCache).forEach(key => {
          if (key.startsWith(`${careerTalkId}_`)) {
            delete nominationsCache[key];
          }
        });

        // Refresh the current tab data
        const currentTabType = ['pending', 'approved', 'rejected'][activeCollegeTab];
        await fetchNominationsByType(currentTabType, careerTalkId);
      }

      fetchCareerTalks(careerTalksPage, careerTalksRowsPerPage);

    } catch (err) {
      console.error('Error approving college:', err);
      setError('Failed to approve college. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open reject dialog
  const openRejectDialog = (college) => {
    setSelectedCollegeForRejection(college);
    setRejectionReason('');
    setRejectionError('');
    setRejectDialogOpen(true);
  };

  // Handle reject with reason
  const handleRejectWithReason = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError('Please provide a rejection reason');
      return;
    }

    setLoading(true);
    setRejectionError('');

    try {
      const data = {
        reject_reason: rejectionReason
      };

      // Use the reject endpoint with reason
      const response = await axios.put(
        // `http://192.168.2.212:8001/new_support/staff/reject/${selectedCollegeForRejection.id}`,
        `${API_BASE_URL}/reject/${selectedCollegeForRejection.id}`,
        data,
        axiosConfig
      );

      console.log('Reject response:', response.data);

      // Update local state
      const updatedColleges = interestedColleges.map(college =>
        college.id === selectedCollegeForRejection.id
          ? {
            ...college,
            status: 'rejected',
            is_staff_approval: '2',
            reject_reason: rejectionReason
          }
          : college
      );

      setInterestedColleges(updatedColleges);
      setSuccess('College rejected successfully!');

      // Clear cache and refresh
      if (selectedCareerTalk) {
        const careerTalkId = selectedCareerTalk.id;
        Object.keys(nominationsCache).forEach(key => {
          if (key.startsWith(`${careerTalkId}_`)) {
            delete nominationsCache[key];
          }
        });

        // Refresh the current tab data
        const currentTabType = ['pending', 'approved', 'rejected'][activeCollegeTab];
        await fetchNominationsByType(currentTabType, careerTalkId);
      }

      fetchCareerTalks(careerTalksPage, careerTalksRowsPerPage);

      // Close reject dialog
      setRejectDialogOpen(false);
      setSelectedCollegeForRejection(null);
      setRejectionReason('');

    } catch (err) {
      console.error('Error rejecting college:', err);
      setRejectionError(err.response?.data?.message || 'Failed to reject college. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove college approval
  const removeCollegeApproval = async (collegeId, careerTalkId) => {
    setLoading(true);
    setError(null);

    try {
      await axios.put(`${API_BASE_URL}/unapprove/${collegeId}`, {}, axiosConfig);

      const updatedColleges = interestedColleges.map(college =>
        college.id === collegeId
          ? {
            ...college,
            status: 'pending',
            is_staff_approval: '0',
            confirmedDate: null
          }
          : college
      );

      setInterestedColleges(updatedColleges);
      setSuccess('College approval removed successfully!');

      // Clear cache and refresh
      if (selectedCareerTalk) {
        const careerTalkId = selectedCareerTalk.id;
        Object.keys(nominationsCache).forEach(key => {
          if (key.startsWith(`${careerTalkId}_`)) {
            delete nominationsCache[key];
          }
        });

        // Refresh the current tab data
        const currentTabType = ['pending', 'approved', 'rejected'][activeCollegeTab];
        await fetchNominationsByType(currentTabType, careerTalkId);
      }

      fetchCareerTalks(careerTalksPage, careerTalksRowsPerPage);

    } catch (err) {
      console.error('Error removing approval:', err);
      setError('Failed to remove approval. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Old reject function (kept for reference, but now using reject with reason)
  const rejectCollege = async (collegeId, careerTalkId) => {
    // This function is now replaced by openRejectDialog
    // We'll keep it for now but it won't be called
    console.log('rejectCollege function is deprecated, use openRejectDialog instead');
  };

  useEffect(() => {
    fetchCareerTalks(careerTalksPage, careerTalksRowsPerPage);
  }, [activeMainTab, careerTalksPage, careerTalksRowsPerPage]);

  // Handle pagination
  const handleChangeCareerTalksPage = (event, newPage) => {
    setCareerTalksPage(newPage);
  };

  const handleChangeCareerTalksRowsPerPage = (event) => {
    setCareerTalksRowsPerPage(parseInt(event.target.value, 10));
    setCareerTalksPage(0);
  };

  const handleChangeCollegesPage = (event, newPage) => {
    setCollegesPage(newPage);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const timePart = timeString.split(':').slice(0, 2).join(':');
      return new Date(`2000-01-01T${timePart}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  // Handle nomination view
  const handleInstitutionNomination = async (careerTalk) => {
    setSelectedCareerTalk(careerTalk);
    setCollegesPage(0);
    setActiveCollegeTab(0);

    console.log('Opening nominations for career talk:', careerTalk);

    await loadInitialNominations(careerTalk.id);
    setInstitutionNominationOpen(true);
  };

  const handleViewCareerTalkDetails = (careerTalk) => {
    setSelectedCareerTalk(careerTalk);
    setCareerTalkDetailsOpen(true);
  };

  // Get paginated colleges based on active tab
  const getPaginatedColleges = () => {
    if (!Array.isArray(interestedColleges)) return [];

    const startIndex = collegesPage * collegesRowsPerPage;
    const endIndex = startIndex + collegesRowsPerPage;

    return interestedColleges.slice(startIndex, endIndex);
  };

  // Get counts for each tab from cache
  const getTabCounts = () => {
    if (!selectedCareerTalk) return { pending: 0, approved: 0, rejected: 0 };

    const careerTalkId = selectedCareerTalk.id;
    return {
      pending: nominationsCache[`${careerTalkId}_pending`]?.length || 0,
      approved: nominationsCache[`${careerTalkId}_approved`]?.length || 0,
      rejected: nominationsCache[`${careerTalkId}_rejected`]?.length || 0
    };
  };

  // Render Career Talk Table
  const renderCareerTalkTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Card elevation={3}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100 }}>Poster</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Event Link</TableCell>
                  <TableCell>
                    {activeMainTab === 1 ? 'Participated Colleges' : 'Institution Nomination'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {careerTalks.length > 0 ? (
                  careerTalks.map((talk) => {
                    const pendingCount = talk.pending_nominations || 0;

                    return (
                      <TableRow key={talk.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 80,
                              height: 60,
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleViewCareerTalkDetails(talk)}
                          >
                            {talk.poster_url ? (
                              <img
                                src={talk.poster_url}
                                alt={talk.topic}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: 'grey.200',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1
                              }}>
                                <Typography variant="caption" color="text.secondary">
                                  No Poster
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="body2">
                              {formatDate(talk.date)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="body2">
                              {formatTime(talk.start_time)} - {formatTime(talk.end_time)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          {talk.link ? (
                            <Button
                              component="a"
                              href={talk.link.startsWith('http') ? talk.link : `https://${talk.link}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<LinkIcon />}
                              size="small"
                              sx={{ color: 'success.main', textTransform: 'none' }}
                              disabled={!isEventLinkVisible(talk)}
                              title={!isEventLinkVisible(talk) ? "Event link will be available on the event date" : "Join Event"}
                            >
                              {isEventLinkVisible(talk) ? 'Join' : 'Link'}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No link
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<People />}
                            onClick={() => handleInstitutionNomination(talk)}
                            disabled={activeMainTab === 2}
                          >
                            {activeMainTab === 1 ? 'View' : 'View'}
                            {pendingCount > 0 && activeMainTab === 0 && (
                              <Badge
                                badgeContent={pendingCount}
                                color="error"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CalendarToday sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No {['upcoming', 'completed', 'cancelled'][activeMainTab]} career talks
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {careerTalksTotal > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={careerTalksTotal}
              rowsPerPage={careerTalksRowsPerPage}
              page={careerTalksPage}
              onPageChange={handleChangeCareerTalksPage}
              onRowsPerPageChange={handleChangeCareerTalksRowsPerPage}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  // Rejection Reason Dialog
 // SIMPLEST FIX - Use a completely isolated local state
const RejectionDialog = () => {
  const [localReason, setLocalReason] = useState('');
  const [localError, setLocalError] = useState('');
  const [localLoading, setLocalLoading] = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (rejectDialogOpen) {
      setLocalReason('');
      setLocalError('');
      setLocalLoading(false);
    }
  }, [rejectDialogOpen]);

  const handleSubmit = async () => {
    if (!localReason.trim()) {
      setLocalError('Please provide a rejection reason');
      return;
    }

    setLocalLoading(true);
    setLocalError('');

    try {
      const data = {
        reject_reason: localReason
      };

      const response = await axios.put(
        // `http://192.168.2.212:8001/new_support/staff/reject/${selectedCollegeForRejection.id}`,
        `${API_BASE_URL}/reject/${selectedCollegeForRejection.id}`,
        data,
        axiosConfig
      );

      console.log('Reject response:', response.data);

      // Update parent state WITHOUT triggering re-render during typing
      // Use a callback to update parent state
      setInterestedColleges(prev => prev.map(college =>
        college.id === selectedCollegeForRejection.id
          ? {
            ...college,
            status: 'rejected',
            is_staff_approval: '2',
            reject_reason: localReason
          }
          : college
      ));
      
      setSuccess('College rejected successfully!');

      // Clear cache
      if (selectedCareerTalk) {
        const careerTalkId = selectedCareerTalk.id;
        setNominationsCache(prev => {
          const newCache = { ...prev };
          Object.keys(newCache).forEach(key => {
            if (key.startsWith(`${careerTalkId}_`)) {
              delete newCache[key];
            }
          });
          return newCache;
        });

        // Refresh current tab data - do this later, not during typing
        setTimeout(() => {
          const currentTabType = ['pending', 'approved', 'rejected'][activeCollegeTab];
          fetchNominationsByType(currentTabType, careerTalkId);
        }, 100);
      }

      // Close dialog FIRST
      setRejectDialogOpen(false);
      setSelectedCollegeForRejection(null);
      
      // Then update other states after a delay
      setTimeout(() => {
        fetchCareerTalks(careerTalksPage, careerTalksRowsPerPage);
      }, 200);

    } catch (err) {
      console.error('Error rejecting college:', err);
      setLocalError(err.response?.data?.message || 'Failed to reject college. Please try again.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleClose = () => {
    if (!localLoading) {
      setRejectDialogOpen(false);
      setSelectedCollegeForRejection(null);
    }
  };

  return (
    <Dialog
      open={rejectDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // These prevent MUI from managing focus which can cause re-renders
      disableRestoreFocus
      disableEscapeKeyDown={localLoading}
      disablePortal
       sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            zIndex: 9999 // Higher than the nomination modal
          }
        }
      }}
      style={{ zIndex: 9999 }} // Also set on the dialog itself
    
    >
      <DialogTitle>Provide Rejection Reason</DialogTitle>

      <DialogContent>
        {selectedCollegeForRejection && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Rejecting: <strong>{selectedCollegeForRejection.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact Person: {selectedCollegeForRejection.contactPerson}
            </Typography>
          </Box>
        )}

        {/* SIMPLE TEXTAREA - No MUI complications */}
        <Box sx={{ mt: 1 }}>
          <label htmlFor="rejection-reason" style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
            Rejection Reason *
          </label>
          <textarea
            id="rejection-reason"
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            placeholder="Enter the reason for rejecting this nomination..."
            disabled={localLoading}
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: localError ? '2px solid #d32f2f' : '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: localLoading ? '#f5f5f5' : 'white'
            }}
            autoFocus
          />
          {localError && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {localError}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Please provide a reason for rejection
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={localLoading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={localLoading || !localReason.trim()}
          variant="contained"
          color="error"
        >
          {localLoading ? 'Submitting...' : 'Confirm Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
  // Institution Nomination Dialog
  const InstitutionNominationDialog = () => {
    const displayedColleges = getPaginatedColleges();
    const totalCount = interestedColleges.length;
    const tabCounts = getTabCounts();

    return (
      <Dialog
        open={institutionNominationOpen}
        onClose={() => setInstitutionNominationOpen(false)}
        maxWidth="xl"
        fullWidth
        scroll="paper"
  disableEnforceFocus
  disableAutoFocus
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            {activeMainTab === 1 ? 'Participated Colleges - ' : 'Institutional Nominations - '}
            {selectedCareerTalk?.topic}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {formatDate(selectedCareerTalk?.date)} • {selectedCareerTalk?.location}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {activeMainTab === 0 && (
            <Tabs
              value={activeCollegeTab}
              onChange={(e, newValue) =>
                handleCollegeTabChange(newValue, selectedCareerTalk?.id)
              }
              sx={{ mb: 2 }}
            >
              <Tab label={`PENDING (${tabCounts.pending})`} />
              <Tab label={`APPROVED (${tabCounts.approved})`} />
              <Tab label={`REJECTED (${tabCounts.rejected})`} />
            </Tabs>
          )}

          {activeMainTab === 1 && (
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              PARTICIPATED COLLEGES ({totalCount})
            </Typography>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>College Details</strong></TableCell>
                      <TableCell><strong>SPOC Details</strong></TableCell>
                      {activeMainTab === 1 && (
                        <>
                          <TableCell><strong>Additional Info</strong></TableCell>
                          <TableCell><strong>Certificate Issued</strong></TableCell>
                        </>
                      )}
                      <TableCell><strong>Date</strong></TableCell>
                      {activeMainTab === 0 && (
                        <TableCell><strong>Actions</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedColleges.length > 0 ? (
                      displayedColleges.map((college) => (
                        <TableRow key={college.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {college.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {college.location}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="body2" fontWeight="medium">
                                  {college.contactPerson}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Email fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {college.email}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {college.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {activeMainTab === 1 && (
                            <>
                              <TableCell>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <People fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      <strong>Students:</strong> {college.studentsCount}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PhotoCamera fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      <strong>Photograph:</strong> {college.photograph_submitted ? 'Yes' : 'No'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={college.certificate_issued ? "YES" : "NO"}
                                  color={college.certificate_issued ? "success" : "error"}
                                  variant="filled"
                                  size="medium"
                                />
                              </TableCell>
                            </>
                          )}

                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(college.preferredDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Applied: {formatDate(college.created_at)}
                            </Typography>
                          </TableCell>

                          {activeMainTab === 0 && (
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                {(college.status === 'pending' || college.is_staff_approval === '0') && (
                                  <>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      onClick={() => approveCollege(college.id, selectedCareerTalk?.id)}
                                      disabled={loading}
                                      size="small"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      variant="contained"
                                      color="error"
                                      onClick={() => openRejectDialog(college)}
                                      disabled={loading}
                                      size="small"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={activeMainTab === 1 ? 5 : 6}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            No colleges found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalCount > collegesRowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={collegesPage}
                    onPageChange={handleChangeCollegesPage}
                    rowsPerPage={collegesRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setCollegesRowsPerPage(parseInt(event.target.value, 10));
                      setCollegesPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setInstitutionNominationOpen(false)}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Career Talk Details Dialog
  const CareerTalkDetailsDialog = () => (
    <Dialog
      open={careerTalkDetailsOpen}
      onClose={() => setCareerTalkDetailsOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {/* <Typography variant="h6">
          {selectedCareerTalk?.topic}
        </Typography> */}
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {selectedCareerTalk && (
          <img
            src={selectedCareerTalk.poster_url}
            alt={selectedCareerTalk.topic}
            style={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'contain',
              borderRadius: 8
            }}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=No+Poster+Available';
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCareerTalkDetailsOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 2, mb: 2, backgroundColor: '#1976d2', color: 'white' }}>
        <Typography variant="h6">
          Staff Dashboard - College Approvals
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box>
        <Tabs
          value={activeMainTab}
          onChange={(e, newValue) => {
            setActiveMainTab(newValue);
            setCareerTalksPage(0);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="Upcoming" />
          <Tab label="Completed" />
          <Tab label="Cancelled" />
        </Tabs>

        {renderCareerTalkTable()}
      </Box>

      <InstitutionNominationDialog />
      <CareerTalkDetailsDialog />
      <RejectionDialog />

      {/* Snackbar for success messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};

export default CareerTalk_staff;